import React, { useEffect, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

export default function ViewCaseStudy() {
  const nav = useNavigate();

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const [caseStudies, setCaseStudies] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);

  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ---------- INIT ---------- */
  useEffect(() => {
    api.get("/admin/classes").then(res => setClasses(res.data));
  }, []);

  async function loadSubjects(classId) {
    const res = await api.get(`/admin/subjects?class_id=${classId}`);
    setSubjects(res.data);
  }

  async function loadCaseStudies() {
    if (!selectedClass || !selectedSubject) return;

    const res = await api.get("/admin/case-study/list", {
      params: {
        class_id: selectedClass.id,
        subject_id: selectedSubject.id
      }
    });
    setCaseStudies(res.data.items || []);
  }

  useEffect(() => {
    loadCaseStudies();
  }, [selectedSubject]);

  /* ---------- CREATE ---------- */
  async function createCaseStudy() {
    if (!title.trim() || !description.trim())
      return alert("Title & paragraph required");

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", description);
      fd.append("class_id", selectedClass.id);
      fd.append("subject_id", selectedSubject.id);
      if (file) fd.append("file", file);

      await api.post("/admin/case-study", fd);

      alert("✅ Case study created");
      setTitle("");
      setDescription("");
      setFile(null);

      loadCaseStudies();
    } catch {
      alert("❌ Create failed");
    }
    setLoading(false);
  }

  /* ---------- VIEW ---------- */
  async function toggleExpand(cs) {
    if (expanded?.id === cs.id) {
      setExpanded(null);
      return;
    }
    const res = await api.get(`/admin/case-study/${cs.id}`);
    setExpanded(res.data);
  }

  /* ---------- CSV UPLOAD (FIXED) ---------- */
  async function uploadCSV(caseId) {
    if (!csvFile) return alert("CSV file select karo");

    const fd = new FormData();
    fd.append("file", csvFile);
    fd.append("case_study_id", caseId);

    try {
      await api.post("/admin/case-study/upload-questions", fd);
      alert("✅ Questions uploaded successfully");
      setCsvFile(null);

      const res = await api.get(`/admin/case-study/${caseId}`);
      setExpanded(res.data);
    } catch (e) {
      alert("❌ CSV upload failed");
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" style={pageStyle}>
      <h1 className="text-3xl font-bold mb-6">📘 Case Study Management</h1>

      {/* CLASS / SUBJECT */}
      <div className="flex gap-4 mb-6">
        <select
          className="border p-2 rounded"
          onChange={(e) => {
            const c = classes.find(x => x.id == e.target.value);
            setSelectedClass(c);
            setSelectedSubject(null);
            if (c) loadSubjects(c.id);
          }}
        >
          <option value="">Select Class</option>
          {classes.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        {subjects.length > 0 && (
          <select
            className="border p-2 rounded"
            onChange={(e) => {
              const s = subjects.find(x => x.id == e.target.value);
              setSelectedSubject(s);
            }}
          >
            <option value="">Select Subject</option>
            {subjects.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* CREATE */}
      <div className="bg-white shadow rounded-lg p-5 mb-8">
        <h2 className="font-semibold text-lg mb-3">➕ Create New Case Study</h2>

        <input
          className="w-full border p-2 rounded mb-3"
          placeholder="Case Study Title"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />

        <textarea
          className="w-full border p-2 rounded mb-3"
          rows={6}
          placeholder="Case Study Paragraph"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <input type="file" onChange={e => setFile(e.target.files[0])} />

        <button
          onClick={createCaseStudy}
          disabled={loading}
          className="mt-4 px-5 py-2 bg-green-600 text-white rounded"
        >
          Create Case Study
        </button>
      </div>

      {/* LIST */}
      <div className="space-y-6">
        {caseStudies.map(cs => (
          <div key={cs.id} className="bg-white shadow rounded-lg p-5">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-xl">{cs.title}</h3>
              <div className="space-x-3">
                <button className="text-blue-600" onClick={() => toggleExpand(cs)}>
                  {expanded?.id === cs.id ? "Hide" : "View"}
                </button>
                <button onClick={() => nav(`/admin/case-study/edit/${cs.id}`)}>Edit</button>
                <button
                  className="text-red-600"
                  onClick={async () => {
                    await api.delete(`/admin/case-study/${cs.id}`);
                    loadCaseStudies();
                  }}
                >
                  Delete
                </button>
              </div>
            </div>

            {expanded?.id === cs.id && (
              <div className="mt-5">
                <div className="bg-yellow-50 p-4 rounded mb-4">
                  <strong>📘 Paragraph</strong>
                  <p className="mt-2 whitespace-pre-wrap">{expanded.description}</p>
                </div>

                <div>
                  
                  <div className="mt-4">
                    <strong className="text-lg">❓ Questions</strong>

                    {expanded.questions && expanded.questions.length > 0 ? (
                      expanded.questions.map((q, i) => (
                        <div
                          key={q.id}
                          className="mt-3 p-4 border rounded-lg bg-gray-50"
                        >
                          <div className="font-semibold mb-2">
                            {i + 1}. {q.text}
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>A) {q.option_a}</div>
                            <div>B) {q.option_b}</div>
                            <div>C) {q.option_c}</div>
                            <div>D) {q.option_d}</div>
                          </div>

                          <div className="mt-2 text-green-700 text-sm">
                            ✔ Correct: {q.correct} | Marks: {q.marks}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 mt-2">No questions uploaded yet</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <input type="file" accept=".csv" onChange={e => setCsvFile(e.target.files[0])} />
                  <button
                    onClick={() => uploadCSV(cs.id)}
                    className="px-4 py-1 bg-blue-600 text-white rounded"
                  >
                    Upload CSV
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ================= BACKGROUND ================= */
const pageStyle = {
  /*minHeight: "100vh",
  backgroundImage: "url('/eduat10-bg.png')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",*/
  padding: 40,
};
